import type { BaseEnvelope } from './base-patch';
import { fromBaseRecord, fromBaseUuid } from './from-base-context';
import { readItemFieldReference as readItemField } from './item-field-reader';
import { phpJsonTruthy } from './php-json-values';
import { isOwnEncumbranceStatus } from './creature-carrying-capacity';
import { readEncumbranceApplications } from './encumbrance-application-plans';

type Row = Record<string, unknown>;
interface Retirement { status_id: string; status_item_id: string }
export interface EncumbranceStatusAdd {
    targetId: string; statusItemId: string; originId: string; durationInt: number; durationType: string; encounterId: string;
    background: true; stillCurrent(): boolean; onStale(): void;
}
type Plan = Retirement & { kind: 'application' | 'retirement' };
type Job = { envelope: BaseEnvelope; baseRev: number; targetId: string; plans: Plan[]; writer: boolean };
interface Hooks {
    current(id: string, entity: string): BaseEnvelope | undefined;
    remove(statusId: string): Promise<unknown>;
    add?(input: EncumbranceStatusAdd): Promise<unknown>;
    isGmPage(encounterId: string): boolean;
    skip(encounterId: string, targetId: string, reason: string): Promise<unknown>;
}
const stored = (row: Row, key: string): unknown => readItemField({ [key]: [row[key] ?? null] }, key);
const list = (row: Row, key: string): Row[] => {
    const value = stored(row, key);
    return value !== null && typeof value === 'object' ? Object.values(value).filter(fromBaseRecord) : [];
};
function actor(base: Row, targetId: string): Row | undefined {
    const ec = readItemField(base, 'encounter_creatures');
    if (fromBaseRecord(ec) && readItemField(ec, 'encounter_creature_id') === targetId) return base;
    for (const member of list(base, 'encounter_creatures_arr')) {
        const found = actor(member, targetId);
        if (found) return found;
    }
    return undefined;
}

/** The runtime's cold/actor writer claim owns automatic saves; observing tabs only paint.
 * Delaying the save until after paint avoids waiting on our own base-patch queue. */
export function createEncumbranceRetirementRuntime(hooks: Hooks) {
    const queued = new Map<string, Job>();
    const running = new Set<string>(), rebasing = new Set<string>(), attempted = new Set<string>();
    const current = (job: Job): boolean => {
        const latest = hooks.current(job.envelope.entity_id, job.envelope.entity);
        return Boolean(latest && latest.base_rev === job.baseRev && latest.base === job.envelope.base
            && latest.metadata === job.envelope.metadata && latest.engine === job.envelope.engine
            && latest.catalog_version === job.envelope.catalog_version && permission(latest, job.targetId) === true);
    };
    const attemptKey = (job: Job, plan: Plan): string => plan.kind === 'retirement' ? plan.status_id : JSON.stringify([
        job.envelope.entity_id, job.targetId, job.baseRev, job.envelope.engine, job.envelope.catalog_version, plan.status_item_id]);
    const due = (job: Job, plan: Plan): boolean => current(job) && job.plans.some(candidate => candidate.kind === plan.kind
        && candidate.status_item_id === plan.status_item_id && (plan.kind === 'application' || candidate.status_id === plan.status_id));
    function schedule(id: string): void {
        if (running.has(id)) return;
        running.add(id);
        setTimeout(() => { void drain(id); }, 0);
    }
    async function drain(id: string): Promise<void> {
        const job = queued.get(id); queued.delete(id);
        try {
            if (!job) return;
            for (const plan of job.plans) {
                let next = rebasing.has(id) ? queued.get(id) ?? job : job;
                for (let retry = 0; retry <= 2; retry++) {
                    const stillCurrent = (): boolean => current(next);
                    if (!due(next, plan)) break;
                    const source = actor(next.envelope.base, next.targetId), ec = source && readItemField(source, 'encounter_creatures');
                    const encounterId = fromBaseRecord(ec) ? readItemField(ec, 'encounter_id') : null;
                    if (!fromBaseUuid(encounterId) || !hooks.isGmPage(encounterId)) break;
                    const statuses = source && readItemField(source, 'statuses');
                    const rows = fromBaseRecord(statuses) ? list(statuses, 'target') : [];
                    const key = attemptKey(next, plan);
                    if (attempted.has(key)) break;
                    if (plan.kind === 'retirement') {
                        const row = rows.find(row => readItemField(row, 'status_id') === plan.status_id);
                        if (!row || readItemField(row, 'status_item_id') !== plan.status_item_id
                            || !isOwnEncumbranceStatus(row) || phpJsonTruthy(readItemField(row, 'status_deleted'))) break;
                    } else if (rows.some(row => readItemField(row, 'status_item_id') === plan.status_item_id
                        && !phpJsonTruthy(readItemField(row, 'status_deleted')))) break;
                    attempted.add(key);
                    let outcome: unknown, stale = false;
                    try {
                        outcome = plan.kind === 'retirement' ? await hooks.remove(plan.status_id) : await hooks.add?.({
                            targetId: next.targetId, statusItemId: plan.status_item_id, originId: plan.status_item_id,
                            durationInt: 0, durationType: 'permanent', encounterId, background: true, stillCurrent,
                            onStale: () => { stale = true; rebasing.add(id); attempted.delete(key); queued.delete(id); } });
                    } catch { outcome = 'failed'; }
                    if (outcome === 'handled') break;
                    if (stale) {
                        // The save awaited its resync/compile/install. Rebase on that compile's plans, never its old verdict.
                        const refreshed = queued.get(id);
                        if (!refreshed) { attempted.add(key); break; }
                        next = refreshed;
                        if (!due(next, plan)) break;
                        if (retry < 2) {
                            await new Promise<void>(resolve => setTimeout(resolve, 25 + Math.random() * 75));
                            next = queued.get(id) ?? next;
                            continue;
                        }
                        // Suppress another automatic pass for this fresh plan after exhausting the bounded retries.
                        attempted.add(attemptKey(next, plan));
                    }
                    if (stillCurrent() && hooks.isGmPage(encounterId)) {
                        const latest = actor(next.envelope.base, next.targetId), fields = latest && readItemField(latest, 'encounter_creatures');
                        const name = String(readItemField(fromBaseRecord(fields) ? fields : {}, 'encounter_creature_name')
                            || readItemField(latest ?? {}, 'item_name') || 'The creature');
                        try {
                            await hooks.skip(encounterId, next.targetId,
                                `encumbrance-${plan.kind}:${name}:status-${plan.kind === 'retirement' ? 'delete' : 'add'}-save-unconfirmed`);
                            // Saving this log bumps the actor revision too. That install cannot rearm the same failed duty.
                            const logged = queued.get(id);
                            if (logged && current(logged)) attempted.add(attemptKey(logged, plan));
                        }
                        catch { /* Logging cannot blank the entity either. */ }
                    }
                    break;
                }
            }
        } catch {
            // Unavailable inputs never remove a successfully painted entity.
        } finally {
            rebasing.delete(id);
            running.delete(id);
            if (queued.get(id)?.writer) schedule(id);
            else queued.delete(id);
        }
    }
    function permission(envelope: BaseEnvelope, targetId: string): boolean | undefined {
        const viewer = readItemField(envelope as unknown as Row, 'viewer');
        const members = fromBaseRecord(viewer) ? readItemField(viewer, 'members') : null;
        const permissions = envelope.entity === 'inventory' ? readItemField(envelope as unknown as Row, 'viewer_permissions')
            : fromBaseRecord(members) && fromBaseRecord(members[targetId]) ? members[targetId]
                : fromBaseRecord(viewer) ? readItemField(viewer, 'permissions') : null;
        if (!fromBaseRecord(permissions)) return undefined;
        const campaign = readItemField(permissions, 'campaign'), canEdit = readItemField(permissions, 'can_edit');
        return typeof campaign === 'boolean' ? campaign : typeof canEdit === 'boolean' ? canEdit : undefined;
    }
    function settle(envelope: BaseEnvelope, computed: unknown, writer: boolean): string[] {
        const missing: string[] = [];
        if (!fromBaseRecord(computed)) return missing;
        const claim = readItemField(envelope as unknown as Row, 'status_retirement_writer');
        if (fromBaseRecord(claim)) writer = readItemField(claim, 'base_rev') === envelope.base_rev
            && readItemField(claim, 'claimed') === true;
        const ec = readItemField(computed, 'encounter_creatures'), bucket = readItemField(computed, 'computed');
        const cache = fromBaseRecord(bucket) ? readItemField(bucket, '_cache') : null;
        const targetId = fromBaseRecord(ec) ? readItemField(ec, 'encounter_creature_id') : null;
        if (fromBaseUuid(targetId) && (fromBaseRecord(cache) || rebasing.has(targetId))) {
            const plans: Plan[] = list(fromBaseRecord(cache) ? cache : {}, 'encumbrance_retirements').filter(row =>
                fromBaseUuid(readItemField(row, 'status_id')) && fromBaseUuid(readItemField(row, 'status_item_id')))
                .map(row => ({ kind: 'retirement', status_id: String(stored(row, 'status_id')), status_item_id: String(stored(row, 'status_item_id')) }));
            plans.push(...readEncumbranceApplications(computed).map(plan => ({ ...plan, kind: 'application' as const })));
            if (plans.length || rebasing.has(targetId)) {
                if (plans.length && permission(envelope, targetId) === undefined) return [`encumbrance-status-${plans[0]!.kind}-plan:viewer-permissions`];
                if (!writer && !rebasing.has(targetId)) return missing;
                queued.set(targetId, { envelope, baseRev: envelope.base_rev, targetId, plans, writer });
                if (plans.length) schedule(targetId);
            }
        }
        for (const member of list(computed, 'encounter_creatures_arr')) missing.push(...settle(envelope, member, writer));
        return missing;
    }
    return { settle };
}
